import React, { useState, useContext, useEffect } from 'react';
import { doc, updateDoc, getDoc, collection, getDocs, query, where, orderBy, limit, deleteDoc, addDoc, increment, serverTimestamp } from 'firebase/firestore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSearch, 
  faPlus, 
  faTimes, 
  faTrash,
  faUserPlus,
  faSpinner,
  faUsers,
  faEdit,
  faLock,
  faUser,
  faCalendarAlt,
  faArrowLeft,
  faCheck,
  faUserShield,
  faInfoCircle,
  faArrowRight
} from '@fortawesome/free-solid-svg-icons';
import { liteClient } from 'algoliasearch/lite';
import { UserContext } from '../../context/UserContext';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import { createNotification, NOTIFICATION_TYPES } from '../../utils/NotificationService';
import verifiedBadge from '../../assets/Images/profileverifiedtick.png';
import defaultUserImage from '../../assets/Images/defaultUserProfilePicture.png';
import LeftMenu from '../LeftMenu';
import RightMenu from '../RightMenu';
import styles from './VendorStaff.module.css';
import AuthCheckWrapper from '../AuthCheckWrapper';
import { useNavigate } from 'react-router-dom';

// Initialize Algolia
const client = liteClient("6DJNAZSX4P", "342932cbe26f7751ec87fa2b1ff32e4c");

// Permission Denied Component
const PermissionDenied = () => (
  <div className={styles.permissionDenied}>
    <FontAwesomeIcon icon={faLock} className={styles.lockIcon} />
    <h2>Access Restricted</h2>
    <p>You don't have permission to view this page. This section is only available to Vendor accounts.</p>
  </div>
);

const VendorStaff = () => {
  const [activeTab, setActiveTab] = useState('current');
  const [isStaffModalVisible, setIsStaffModalVisible] = useState(false);
  const [staff, setStaff] = useState([]);
  const [displayedStaff, setDisplayedStaff] = useState([]);
  const [pastStaff, setPastStaff] = useState([]);
  const [displayedPastStaff, setDisplayedPastStaff] = useState([]);
  const [hasMoreStaff, setHasMoreStaff] = useState(false);
  const [hasMorePastStaff, setHasMorePastStaff] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [staffCount, setStaffCount] = useState(0);
  const [pastStaffCount, setPastStaffCount] = useState(0);
  const { userData } = useContext(UserContext);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [tours, setTours] = useState([]);
  const navigate = useNavigate();
  const [hasAccess, setHasAccess] = useState(false);
  
  // Staff search states
  const [staffSearchQuery, setStaffSearchQuery] = useState('');
  const [staffResults, setStaffResults] = useState([]);
  const [searchingStaff, setSearchingStaff] = useState(false);
  const [isMoveToPastModalVisible, setIsMoveToPastModalVisible] = useState(false);
  const [selectedStaffForMove, setSelectedStaffForMove] = useState(null);

  // States for selected staff in modal
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [staffPermissions, setStaffPermissions] = useState({
    canAddItems: false,
    canEditItems: false,
    canDeleteItems: false,
    canChangeStatus: false
  });
  
  // For dates - current and past staff
  const [sinceMonth, setSinceMonth] = useState('');
  const [sinceYear, setSinceYear] = useState('');
  const [startMonth, setStartMonth] = useState('');
  const [startYear, setStartYear] = useState('');
  const [endMonth, setEndMonth] = useState('');
  const [endYear, setEndYear] = useState('');

  // Check if user has access based on userType
  useEffect(() => {
    if (userData) {
      setHasAccess(userData.userType === 'Vendor');
    }
  }, [userData]);

  // Check for screen size changes
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Load staff data with subcollection pattern
  useEffect(() => {
    if (!userData?.userId) return;
    
    const loadStaffData = async () => {
      setIsProcessing(true);
      try {
        const db = getFirestoreInstance();
        
        // First get the user document to get the count and top 3 staff
        const userDoc = await getDoc(doc(db, 'users', userData.userId));
        let initialStaff = [];
        let initialPastStaff = [];
        let totalStaffCount = 0;
        let totalPastStaffCount = 0;
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          initialStaff = userData.vendorStaff || [];
          initialPastStaff = userData.pastVendorStaff || [];
          totalStaffCount = userData.vendorStaffCount || 0;
          totalPastStaffCount = userData.pastVendorStaffCount || 0;
          
          // Set the count states
          setStaffCount(totalStaffCount);
          setPastStaffCount(totalPastStaffCount);
        }
        
        // Set the initial displayed staff
        setDisplayedStaff(initialStaff);
        setDisplayedPastStaff(initialPastStaff);
        
        // Check if there are more staff than what's displayed
        setHasMoreStaff(totalStaffCount > initialStaff.length);
        setHasMorePastStaff(totalPastStaffCount > initialPastStaff.length);
        
        // Load pending requests from pendingVendorStaff subcollection
        const pendingStaffRef = collection(db, 'users', userData.userId, 'pendingVendorStaff');
        const pendingStaffSnapshot = await getDocs(pendingStaffRef);
        
        if (!pendingStaffSnapshot.empty) {
          // Process pending staff
          const pendingStaff = await Promise.all(
            pendingStaffSnapshot.docs.map(async (docSnapshot) => {
              const staffData = docSnapshot.data();
              
              // Always fetch the latest data for the staff
              if (staffData.userId) {
                try {
                  const userDoc = await getDoc(doc(db, 'users', staffData.userId));
                  if (userDoc.exists()) {
                    const userData = userDoc.data();
                    return {
                      id: docSnapshot.id,
                      userId: staffData.userId,
                      fullName: userData.fullName || '',
                      userName: userData.userName || '',
                      profilePictureUrl: userData.profilePictureUrl || '',
                      userSubType: userData.userSubType || '',
                      email: userData.email || '',
                      idVerificationStatus: userData.idVerificationStatus || '',
                      status: staffData.status || 'pending',
                      role: staffData.role || 'staff',
                      permissions: staffData.permissions || {},
                      createdAt: staffData.createdAt || new Date().toISOString(),
                      since: staffData.since || null,
                      isPending: true, // Mark as pending for UI
                      isPast: staffData.isPast || false
                    };
                  }
                } catch (error) {
                  console.error(`Error fetching user data for ${staffData.userId}:`, error);
                }
              }
              
              // Return the staff data as is if user data enrichment failed
              return {
                id: docSnapshot.id,
                ...staffData,
                isPending: true
              };
            })
          );
          
          // Add pending staff to the display (separated by current/past)
          const currentPendingStaff = pendingStaff.filter(staff => !staff.isPast);
          const pastPendingStaff = pendingStaff.filter(staff => staff.isPast);
          
          // Update displayed staff with pending ones at the top
          if (currentPendingStaff.length > 0) {
            setDisplayedStaff(prev => [...currentPendingStaff, ...prev]);
          }
          
          if (pastPendingStaff.length > 0) {
            setDisplayedPastStaff(prev => [...pastPendingStaff, ...prev]);
          }
        }
        
        // If we need to display staff, fetch them from subcollection
        if (initialStaff.length > 0) {
          // Fetch all current staff from subcollection for complete data
          const staffRef = collection(db, 'users', userData.userId, 'vendorStaff');
          const staffQuery = query(
            staffRef,
            where('isPast', '==', false),
            orderBy('createdAt', 'desc')
          );
          
          const staffSnapshot = await getDocs(staffQuery);
          
          if (!staffSnapshot.empty) {
            const allStaff = await Promise.all(
              staffSnapshot.docs.map(async (docSnapshot) => {
                const staffData = docSnapshot.data();
                
                // Enrich with FRESH user data every time
                if (staffData.userId) {
                  try {
                    const userDoc = await getDoc(doc(db, 'users', staffData.userId));
                    if (userDoc.exists()) {
                      const userData = userDoc.data();
                      return {
                        id: docSnapshot.id,
                        userId: staffData.userId,
                        fullName: userData.fullName || '',
                        userName: userData.userName || '',
                        profilePictureUrl: userData.profilePictureUrl || '',
                        userSubType: userData.userSubType || '',
                        email: userData.email || '',
                        idVerificationStatus: userData.idVerificationStatus || '',
                        status: staffData.status || 'pending',
                        role: staffData.role || 'staff',
                        permissions: staffData.permissions || {},
                        createdAt: staffData.createdAt || new Date().toISOString(),
                        since: staffData.since || null
                      };
                    }
                  } catch (error) {
                    console.error(`Error fetching user data for ${staffData.userId}:`, error);
                  }
                }
                
                // Return the staff data as is if user data enrichment failed
                return {
                  id: docSnapshot.id,
                  ...staffData
                };
              })
            );
            
            // Set the full staff list
            setStaff(allStaff);
          } else {
            setStaff(initialStaff);
          }
        }
        
        // Similar logic for past staff - fetch fresh data every time
        if (initialPastStaff.length > 0) {
          // Fetch all past staff from subcollection for complete data
          const pastStaffRef = collection(db, 'users', userData.userId, 'vendorStaff');
          const pastStaffQuery = query(
            pastStaffRef,
            where('isPast', '==', true),
            orderBy('createdAt', 'desc')
          );
          
          const pastStaffSnapshot = await getDocs(pastStaffQuery);
          
          if (!pastStaffSnapshot.empty) {
            const allPastStaff = await Promise.all(
              pastStaffSnapshot.docs.map(async (docSnapshot) => {
                const staffData = docSnapshot.data();
                
                // Enrich with user data
                if (staffData.userId) {
                  try {
                    const userDoc = await getDoc(doc(db, 'users', staffData.userId));
                    if (userDoc.exists()) {
                      const userData = userDoc.data();
                      return {
                        id: docSnapshot.id,
                        userId: staffData.userId,
                        fullName: userData.fullName || '',
                        userName: userData.userName || '',
                        profilePictureUrl: userData.profilePictureUrl || '',
                        userSubType: userData.userSubType || '',
                        email: userData.email || '',
                        idVerificationStatus: userData.idVerificationStatus || '',
                        status: staffData.status || 'pending',
                        role: staffData.role || 'staff',
                        permissions: staffData.permissions || {},
                        createdAt: staffData.createdAt || new Date().toISOString(),
                        duration: staffData.duration || null
                      };
                    }
                  } catch (error) {
                    console.error(`Error fetching user data for ${staffData.userId}:`, error);
                  }
                }
                
                // Return the staff data as is if user data enrichment failed
                return {
                  id: docSnapshot.id,
                  ...staffData
                };
              })
            );
            
            // Set the full past staff list
            setPastStaff(allPastStaff);
          } else {
            setPastStaff(initialPastStaff);
          }
        }
      } catch (error) {
        console.error('Error fetching staff data:', error);
      } finally {
        setIsProcessing(false);
      }
    };
    
    loadStaffData();
  }, [userData?.userId]);

  // Add function to navigate to staff profile
  const navigateToUserProfile = (userName) => {
    navigate(`/${userName}`);
  };

  const handleStaffSearch = async () => {
    if (!staffSearchQuery.trim()) return;
    
    try {
      setSearchingStaff(true);
      const { results } = await client.search([{
        indexName: 'users_index',
        query: staffSearchQuery,
        filters: 'userType:Staff',
        hitsPerPage: 10
      }]);
  
      const formattedStaff = results[0].hits.map(hit => ({
        id: hit.objectID,
        fullName: hit.fullName,
        email: hit.email,
        userName: hit.userName,
        userSubType: hit.userSubType,
        profilePictureUrl: hit.profilePictureUrl,
        userType: hit.userType,
        idVerificationStatus: hit.idVerificationStatus,
        isAlreadyAdded: staff.some(staff => staff.userId === hit.objectID) ||
                       pastStaff.some(staff => staff.userId === hit.objectID)
      }));
      
      setStaffResults(formattedStaff);
    } catch (error) {
      console.error('Error searching staff:', error);
      setStaffResults([]);
    } finally {
      setSearchingStaff(false);
    }
  };

  // Load more staff from subcollection
  const loadMoreStaff = async () => {
    if (isLoadingMore || !userData?.userId) return;
    
    setIsLoadingMore(true);
    try {
      if (activeTab === 'current') {
        // Skip loading more if we already have all staff loaded
        if (displayedStaff.length >= staffCount) {
          setHasMoreStaff(false);
          return;
        }
        
        // Load the next batch of staff
        const nextBatch = staff.slice(
          displayedStaff.length,
          displayedStaff.length + 6
        );
        
        setDisplayedStaff(prev => [...prev, ...nextBatch]);
        
        // Check if there are more staff to load
        setHasMoreStaff(displayedStaff.length + nextBatch.length < staffCount);
      } else {
        // Skip loading more if we already have all past staff loaded
        if (displayedPastStaff.length >= pastStaffCount) {
          setHasMorePastStaff(false);
          return;
        }
        
        // Load the next batch of past staff
        const nextBatch = pastStaff.slice(
          displayedPastStaff.length,
          displayedPastStaff.length + 6
        );
        
        setDisplayedPastStaff(prev => [...prev, ...nextBatch]);
        
        // Check if there are more past staff to load
        setHasMorePastStaff(displayedPastStaff.length + nextBatch.length < pastStaffCount);
      }
    } catch (error) {
      console.error(`Error loading more ${activeTab} staff:`, error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Handle initial selection of a staff member
  const handleInitialSelection = (staff) => {
    setSelectedStaff(staff);
    
    // Reset form fields
    setSinceMonth('');
    setSinceYear('');
    setStartMonth('');
    setStartYear('');
    setEndMonth('');
    setEndYear('');
    
    // Reset permissions
    setStaffPermissions({
      canEditProfile: false,
      canManageEvents: false,
      canManageStaff: false,
      canManageActs: false,
      canViewFinancials: false,
      canEditFinancials: false
    });
  };

  // Handle permission toggle
  const handlePermissionToggle = (permission) => {
    setStaffPermissions(prev => ({
      ...prev,
      [permission]: !prev[permission]
    }));
  };

  // Handle submission of the staff addition form
  const handleSubmitStaff = async () => {
    if (!selectedStaff) return;
    
    // Validate form fields
    if (activeTab === 'current') {
      // Validate current association fields
      if (!sinceMonth || !sinceYear) {
        alert('Please fill in the since date');
        return;
      }
    } else {
      // Validate past association fields
      if (!startMonth || !startYear || !endMonth || !endYear) {
        alert('Please fill in all date fields');
        return;
      }
      
      // Validate date logic
      const startDate = new Date(parseInt(startYear), parseInt(startMonth) - 1);
      const endDate = new Date(parseInt(endYear), parseInt(endMonth) - 1);
      
      if (startDate > endDate) {
        alert('Start date must be before end date');
        return;
      }
    }
    
    setIsProcessing(true);
    try {
      const db = getFirestoreInstance();
      
      // Prepare staff data
      const staffData = {
        userId: selectedStaff.id,
        status: 'pending',
        role: 'staff',
        permissions: staffPermissions,
        isPast: activeTab === 'past',
        createdAt: serverTimestamp()
      };
      
      // Add date information
      if (activeTab === 'current') {
        staffData.since = {
          month: sinceMonth,
          year: sinceYear
        };
      } else {
        staffData.duration = {
          startMonth,
          startYear,
          endMonth,
          endYear
        };
      }
      
      // Format date for message
      const formattedDate = activeTab === 'current'
        ? `Since: ${sinceMonth}/${sinceYear}`
        : `From: ${startMonth}/${startYear} To: ${endMonth}/${endYear}`;
      
      // Format permissions for message
      const permissionsText = Object.entries(staffPermissions)
        .filter(([_, value]) => value)
        .map(([key]) => {
          return key
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, str => str.toUpperCase());
        })
        .join(', ');
      
      const notificationMessage = `${userData.fullName || 'A vendor'} (${userData.userSubType}) wants to add you as their ${activeTab === 'current' ? '' : 'past '}staff member. ${formattedDate}${permissionsText ? ` Permissions: ${permissionsText}` : ''}`;
      
      // Create the title based on request type
      const notificationTitle = activeTab === 'current' 
        ? 'Vendor Staff Request' 
        : 'Past Vendor Staff Request';
      
      // Add to pendingVendorStaff subcollection
      const pendingStaffRef = collection(db, 'users', userData.userId, activeTab === 'current' ? 'pendingVendorStaff' : 'pendingPastVendorStaff');
      const pendingStaffDoc = await addDoc(pendingStaffRef, {
        ...staffData,
        // Store entity display data for UI
        targetData: {
          fullName: selectedStaff.fullName || '',
          userName: selectedStaff.userName || '',
          profilePictureUrl: selectedStaff.profilePictureUrl || '',
          userSubType: selectedStaff.userSubType || '',
          idVerificationStatus: selectedStaff.idVerificationStatus || ''
        }
      });
      
      // Now use createNotification from NotificationService
      const notificationResponse = await createNotification(selectedStaff.id, 'general', {
        type: activeTab === 'current' ? NOTIFICATION_TYPES.VENDOR_STAFF_REQUEST : NOTIFICATION_TYPES.PAST_VENDOR_STAFF_REQUEST,
        title: notificationTitle,
        message: notificationMessage,
        permissions: staffPermissions,
        organizationId: userData.userId,
        organizationName: userData.fullName || '',
        organizationType: userData.userSubType || '',
        userId: selectedStaff.id,
        isPast: activeTab === 'past',
        status: 'pending',
        requestId: pendingStaffDoc.id,
        createdBy: {
          userId: userData.userId,
          fullName: userData.fullName,
          userName: userData.userName,
          profilePictureUrl: userData.profilePictureUrl || ''
        },
        data: {
          staffData: {
            staffId: pendingStaffDoc.id
          },
          _displayData: {
            vendorName: userData.fullName,
            vendorType: userData.userSubType,
            isPast: activeTab === 'past',
            permissions: staffPermissions,
            ...(activeTab === 'current' 
              ? { sinceMonth, sinceYear } 
              : { startMonth, startYear, endMonth, endYear })
          }
        }
      });
      
      // Update the pending request document with the notification ID
      await updateDoc(doc(db, 'users', userData.userId, 
        activeTab === 'current' ? 'pendingVendorStaff' : 'pendingPastVendorStaff', 
        pendingStaffDoc.id), {
        notificationId: notificationResponse.id
      });
      
      // Store the enriched data for UI updates
      const enrichedStaffData = {
        id: pendingStaffDoc.id,
        ...staffData,
        // We'll still need these for UI display
        fullName: selectedStaff.fullName || '',
        userName: selectedStaff.userName || '',
        profilePictureUrl: selectedStaff.profilePictureUrl || '',
        userSubType: selectedStaff.userSubType || '',
        email: selectedStaff.email || '',
        idVerificationStatus: selectedStaff.idVerificationStatus || '',
        isPending: true, // Mark as pending for UI
        // Store notification doc ID for direct access
        notificationId: notificationResponse.id
      };
      
      // Update the appropriate state arrays for UI
      if (activeTab === 'current') {
        setStaff(prev => [enrichedStaffData, ...prev]);
        
        // Increment the staff count
        const newStaffCount = staffCount + 1;
        setStaffCount(newStaffCount);
        
        // Update displayed staff
        setDisplayedStaff(prev => [enrichedStaffData, ...prev]);
        
        // Update hasMoreStaff based on the new count
        setHasMoreStaff(newStaffCount > displayedStaff.length + 1);
      } else {
        setPastStaff(prev => [enrichedStaffData, ...prev]);
        
        // Increment the past staff count
        const newPastStaffCount = pastStaffCount + 1;
        setPastStaffCount(newPastStaffCount);
        
        // Update displayed past staff
        setDisplayedPastStaff(prev => [enrichedStaffData, ...prev]);
        
        // Update hasMorePastStaff based on the new count
        setHasMorePastStaff(newPastStaffCount > displayedPastStaff.length + 1);
      }
      
      // Reset search form
      setStaffSearchQuery('');
      setStaffResults([]);
      setSelectedStaff(null);
      
      // Close modal
      setIsStaffModalVisible(false);
      
      alert(`Staff request sent successfully! Waiting for approval.`);
    } catch (error) {
      console.error('Error adding staff:', error);
      alert('Failed to add staff member. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRemoveStaff = async (staffId) => {
    if (!window.confirm('Are you sure you want to remove this staff member?')) return;
    
    const staff = activeTab === 'current' 
      ? displayedStaff.find(s => s.id === staffId)
      : displayedPastStaff.find(s => s.id === staffId);
    
    if (!staff) {
      console.error('Staff not found:', staffId);
      return;
    }
    
    setIsProcessing(true);
    try {
      const db = getFirestoreInstance();
      
      // Handle pending request
      if (staff.isPending) {
        // Delete from pendingVendorStaff subcollection
        const pendingStaffRef = doc(db, 'users', userData.userId, activeTab === 'current' ? 'pendingVendorStaff' : 'pendingPastVendorStaff', staff.id);
        await deleteDoc(pendingStaffRef);
        
        // Delete notification
        if (staff.notificationId) {
          try {
            await deleteDoc(doc(db, 'users', staff.userId, 'notifications', staff.notificationId));
          } catch (error) {
            console.error('Error deleting notification:', error);
            // Continue even if notification deletion fails
          }
        }
        
        // Update UI by removing this staff
        if (activeTab === 'current') {
          setDisplayedStaff(prev => prev.filter(s => s.id !== staff.id));
          setStaff(prev => prev.filter(s => s.id !== staff.id));
        } else {
          setDisplayedPastStaff(prev => prev.filter(s => s.id !== staff.id));
          setPastStaff(prev => prev.filter(s => s.id !== staff.id));
        }
        
        alert('Pending request canceled successfully');
        setIsProcessing(false);
        return;
      }
      
      // For non-pending (accepted) staff, handle removal
      const userRef = doc(db, 'users', userData.userId);
      
      // IMPORTANT FIX: Check if staff exists in the vendorStaff subcollection
      // using the ID from the staff object, NOT the ID from associatedActs
      const staffRef = doc(db, 'users', userData.userId, 'vendorStaff', staff.id);
      const staffDoc = await getDoc(staffRef);
      
      if (!staffDoc.exists()) {
        console.error('Staff document not found in vendorStaff subcollection:', staff.id);
        throw new Error('Staff document not found in subcollection');
      }
      
      const staffData = staffDoc.data();
      
      // If we have stored otherSideDocId, use it for direct access to the other side document
      const staffUserId = staffData.userId;
      const otherSideDocId = staffData.otherSideDocId;
      
      // 1. Delete from vendorStaff subcollection
      await deleteDoc(staffRef);
      
      // 2. If we have the ID for the other side document, delete it directly
      if (staffUserId && otherSideDocId) {
        // Delete from staff's associatedVendors collection using the otherSideDocId
        // This is the crucial fix - using the correct document ID for the associatedVendors subcollection
        await deleteDoc(doc(db, 'users', staffUserId, 'associatedVendors', otherSideDocId));
      } else if (staffUserId) {
        // If we don't have otherSideDocId, search for the corresponding document
        try {
          const associatedVendorsRef = collection(db, 'users', staffUserId, 'associatedVendors');
          const associatedVendorsQuery = query(associatedVendorsRef, where('vendorId', '==', userData.userId));
          const associatedVendorsSnapshot = await getDocs(associatedVendorsQuery);
          
          if (!associatedVendorsSnapshot.empty) {
            // Delete all matching entries (should be just one, but to be safe)
            for (const docSnap of associatedVendorsSnapshot.docs) {
              await deleteDoc(docSnap.ref);
            }
          }
        } catch (error) {
          console.error('Error finding and deleting associated vendor document:', error);
          // Continue even if this lookup fails
        }
      }
      
      // 3. Update main document and states
      const isPast = activeTab === 'past';
      
      if (isPast) {
        // Update both state arrays
        setPastStaff(prev => prev.filter(s => s.id !== staff.id));
        setDisplayedPastStaff(prev => prev.filter(s => s.id !== staff.id));
        
        // Decrement the staff count
        const newPastStaffCount = Math.max(0, pastStaffCount - 1);
        setPastStaffCount(newPastStaffCount);
        
        // Get new top 3 past staff for main document
        const staffColRef = collection(db, 'users', userData.userId, 'vendorStaff');
        const top3Query = query(
          staffColRef,
          where('isPast', '==', true),
          orderBy('createdAt', 'desc'),
          limit(3)
        );
        
        const top3Snapshot = await getDocs(top3Query);
        const top3PastStaff = await Promise.all(
          top3Snapshot.docs.map(async (docSnapshot) => {
            const staffData = docSnapshot.data();
            
            // Enrich with user data
            if (staffData.userId) {
              try {
                const userDoc = await getDoc(doc(db, 'users', staffData.userId));
                if (userDoc.exists()) {
                  const userData = userDoc.data();
                  return {
                    id: docSnapshot.id,
                    ...staffData,
                    fullName: userData.fullName || '',
                    userName: userData.userName || '',
                    profilePictureUrl: userData.profilePictureUrl || '',
                    userSubType: userData.userSubType || '',
                    idVerificationStatus: userData.idVerificationStatus || ''
                  };
                }
              } catch (error) {
                console.error(`Error fetching user data for ${staffData.userId}:`, error);
              }
            }
            
            return {
              id: docSnapshot.id,
              ...staffData
            };
          })
        );
        
        // 3. Update main document with top3 and the count for past staff
        await updateDoc(userRef, {
          pastVendorStaff: top3PastStaff,
          pastVendorStaffCount: increment(-1)
        });
        
        // 4. Send notification about removal
        await sendRemovalNotification(staffUserId, true);
        
        // 5. Update staff user's main document with top 3 past vendors
        if (staffUserId) {
          try {
            // Get top 3 past associated vendors for the staff
            const associatedVendorsRef = collection(db, 'users', staffUserId, 'associatedVendors');
            const pastVendorsQuery = query(
              associatedVendorsRef,
              where('isPast', '==', true),
              orderBy('createdAt', 'desc'),
              limit(3)
            );
            
            const pastVendorsSnapshot = await getDocs(pastVendorsQuery);
            const top3PastVendors = await Promise.all(
              pastVendorsSnapshot.docs.map(async (docSnapshot) => {
                const vendorData = docSnapshot.data();
                
                // Enrich with vendor data
                if (vendorData.vendorId) {
                  try {
                    const vendorDoc = await getDoc(doc(db, 'users', vendorData.vendorId));
                    if (vendorDoc.exists()) {
                      const vendorUserData = vendorDoc.data();
                      return {
                        id: docSnapshot.id,
                        ...vendorData,
                        fullName: vendorUserData.fullName || '',
                        userName: vendorUserData.userName || '',
                        profilePictureUrl: vendorUserData.profilePictureUrl || '',
                        userSubType: vendorUserData.userSubType || ''
                      };
                    }
                  } catch (error) {
                    console.error(`Error fetching vendor data for ${vendorData.vendorId}:`, error);
                  }
                }
                
                return {
                  id: docSnapshot.id,
                  ...vendorData
                };
              })
            );
            
            // Update staff user's main document
            await updateDoc(doc(db, 'users', staffUserId), {
              pastAssociatedVendors: top3PastVendors,
              pastAssociatedVendorsCount: increment(-1)
            });
          } catch (error) {
            console.error('Error updating staff pastAssociatedVendors:', error);
            // Continue even if this fails
          }
        }
        
        // Update hasMorePastStaff based on the new count
        setHasMorePastStaff(newPastStaffCount > top3PastStaff.length);
      } else {
        // Handle current staff
        setStaff(prev => prev.filter(s => s.id !== staff.id));
        setDisplayedStaff(prev => prev.filter(s => s.id !== staff.id));
        
        // Decrement the staff count
        const newStaffCount = Math.max(0, staffCount - 1);
        setStaffCount(newStaffCount);
        
        // Get new top 3 staff for main document
        const staffColRef = collection(db, 'users', userData.userId, 'vendorStaff');
        const top3Query = query(
          staffColRef,
          where('isPast', '==', false),
          orderBy('createdAt', 'desc'),
          limit(3)
        );
        
        const top3Snapshot = await getDocs(top3Query);
        const top3Staff = await Promise.all(
          top3Snapshot.docs.map(async (docSnapshot) => {
            const staffData = docSnapshot.data();
            
            // Enrich with user data
            if (staffData.userId) {
              try {
                const userDoc = await getDoc(doc(db, 'users', staffData.userId));
                if (userDoc.exists()) {
                  const userData = userDoc.data();
                  return {
                    id: docSnapshot.id,
                    ...staffData,
                    fullName: userData.fullName || '',
                    userName: userData.userName || '',
                    profilePictureUrl: userData.profilePictureUrl || '',
                    userSubType: userData.userSubType || '',
                    idVerificationStatus: userData.idVerificationStatus || ''
                  };
                }
              } catch (error) {
                console.error(`Error fetching user data for ${staffData.userId}:`, error);
              }
            }
            
            return {
              id: docSnapshot.id,
              ...staffData
            };
          })
        );
        
        // Update main document with top3 and the count
        await updateDoc(userRef, {
          vendorStaff: top3Staff,
          vendorStaffCount: increment(-1)
        });
        
        // Send notification about removal
        await sendRemovalNotification(staffUserId, false);
        
        // Update staff user's main document with top 3 current vendors
        if (staffUserId) {
          try {
            // Get top 3 current associated vendors for the staff
            const associatedVendorsRef = collection(db, 'users', staffUserId, 'associatedVendors');
            const currentVendorsQuery = query(
              associatedVendorsRef,
              where('isPast', '==', false),
              orderBy('createdAt', 'desc'),
              limit(3)
            );
            
            const currentVendorsSnapshot = await getDocs(currentVendorsQuery);
            const top3CurrentVendors = await Promise.all(
              currentVendorsSnapshot.docs.map(async (docSnapshot) => {
                const vendorData = docSnapshot.data();
                
                // Enrich with vendor data
                if (vendorData.vendorId) {
                  try {
                    const vendorDoc = await getDoc(doc(db, 'users', vendorData.vendorId));
                    if (vendorDoc.exists()) {
                      const vendorUserData = vendorDoc.data();
                      return {
                        id: docSnapshot.id,
                        ...vendorData,
                        fullName: vendorUserData.fullName || '',
                        userName: vendorUserData.userName || '',
                        profilePictureUrl: vendorUserData.profilePictureUrl || '',
                        userSubType: vendorUserData.userSubType || ''
                      };
                    }
                  } catch (error) {
                    console.error(`Error fetching vendor data for ${vendorData.vendorId}:`, error);
                  }
                }
                
                return {
                  id: docSnapshot.id,
                  ...vendorData
                };
              })
            );
            
            // Update staff user's main document
            await updateDoc(doc(db, 'users', staffUserId), {
              associatedVendors: top3CurrentVendors,
              associatedVendorsCount: increment(-1)
            });
          } catch (error) {
            console.error('Error updating staff associatedVendors:', error);
            // Continue even if this fails
          }
        }
        
        // Update hasMoreStaff based on the new count
        setHasMoreStaff(newStaffCount > top3Staff.length);
      }
      
      alert(`Staff member removed successfully`);
    } catch (error) {
      console.error('Error removing staff:', error);
      alert('Failed to remove staff member. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };
  
  // Helper function to send removal notifications
  const sendRemovalNotification = async (staffId, isPast) => {
    try {
      const db = getFirestoreInstance();
      const messagePrefix = isPast ? 'past ' : '';
      
      // Create notification for the staff
      await addDoc(collection(db, 'users', staffId, 'notifications'), {
        type: isPast ? NOTIFICATION_TYPES.PAST_VENDOR_STAFF_REMOVAL : NOTIFICATION_TYPES.VENDOR_STAFF_REMOVAL,
        title: `${isPast ? 'Past ' : ''}Vendor Relationship Removed`,
        message: `${userData.fullName} has removed you from their ${messagePrefix}staff.`,
        organizationId: userData.userId,
        userId: staffId,
        status: 'removed',
        timestamp: new Date().toISOString(),
        data: {
          _displayData: {
            vendorName: userData.fullName,
            vendorType: userData.userSubType
          }
        }
      });
      
      return true;
    } catch (error) {
      console.error('Error sending removal notification:', error);
      return false;
    }
  };
  
  // Handle permission updates for an existing staff member
  const handleUpdatePermissions = async (staffId, newPermissions) => {
    if (!window.confirm('Are you sure you want to update permissions for this staff member?')) return;
    
    setIsProcessing(true);
    try {
      const db = getFirestoreInstance();
      
      // Find the staff in the appropriate array
      const staffMember = activeTab === 'current' 
        ? displayedStaff.find(s => s.id === staffId)
        : displayedPastStaff.find(s => s.id === staffId);
      
      if (!staffMember) {
        throw new Error('Staff member not found');
      }
      
      // Update permissions in the vendorStaff subcollection
      const staffRef = doc(db, 'users', userData.userId, 'vendorStaff', staffId);
      await updateDoc(staffRef, {
        permissions: newPermissions,
        updatedAt: new Date().toISOString()
      });
      
      // If there's a reference to the other side document, update it too
      if (staffMember.otherSideDocId) {
        const otherSideRef = doc(db, 'users', staffMember.userId, 'associatedVendors', staffMember.otherSideDocId);
        await updateDoc(otherSideRef, {
          permissions: newPermissions,
          updatedAt: new Date().toISOString()
        });
      }
      
      // Send notification about permission update
      await addDoc(collection(db, 'users', staffMember.userId, 'notifications'), {
        type: NOTIFICATION_TYPES.VENDOR_STAFF_PERMISSIONS_UPDATED,
        title: 'Staff Permissions Updated',
        message: `${userData.fullName} has updated your permissions as a staff member.`,
        organizationId: userData.userId,
        userId: staffMember.userId,
        status: 'updated',
        timestamp: new Date().toISOString(),
        data: {
          permissions: newPermissions,
          _displayData: {
            vendorName: userData.fullName,
            vendorType: userData.userSubType
          }
        }
      });
      
      // Update UI state
      if (activeTab === 'current') {
        setStaff(prev => prev.map(s => 
          s.id === staffId ? { ...s, permissions: newPermissions } : s
        ));
        setDisplayedStaff(prev => prev.map(s => 
          s.id === staffId ? { ...s, permissions: newPermissions } : s
        ));
      } else {
        setPastStaff(prev => prev.map(s => 
          s.id === staffId ? { ...s, permissions: newPermissions } : s
        ));
        setDisplayedPastStaff(prev => prev.map(s => 
          s.id === staffId ? { ...s, permissions: newPermissions } : s
        ));
      }
      
      alert('Staff permissions updated successfully');
    } catch (error) {
      console.error('Error updating staff permissions:', error);
      alert('Failed to update staff permissions. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle moving a staff member to past
  const handleMoveToPast = (staffId) => {
    const staffMember = displayedStaff.find(s => s.id === staffId);
    if (!staffMember) return;
    
    setSelectedStaffForMove(staffMember);
    setIsMoveToPastModalVisible(true);
  };

  // Confirm moving a staff member to past
  const confirmMoveToPast = async () => {
    if (!selectedStaffForMove) return;
    
    setIsProcessing(true);
    try {
      const db = getFirestoreInstance();
      const staff = selectedStaffForMove;
      
      // 1. Create duration from since date and current date
      const currentDate = new Date();
      const duration = {
        startMonth: staff.since?.month || '1',
        startYear: staff.since?.year || '2020',
        endMonth: (currentDate.getMonth() + 1).toString(),
        endYear: currentDate.getFullYear().toString()
      };
      
      // 2. Get the vendorStaff document
      const staffRef = doc(db, 'users', userData.userId, 'vendorStaff', staff.id);
      const staffDoc = await getDoc(staffRef);
      
      if (!staffDoc.exists()) {
        throw new Error('Staff not found in your vendorStaff collection');
      }
      
      // 3. Update the staff document to mark as past
      await updateDoc(staffRef, {
        isPast: true,
        duration: duration,
        movedToPastAt: new Date().toISOString()
      });
      
      // 4. Update counts and arrays in the main document
      const userRef = doc(db, 'users', userData.userId);
      
      // 4.1. Get updated top 3 current staff
      const currentStaffRef = collection(db, 'users', userData.userId, 'vendorStaff');
      const currentTop3Query = query(
        currentStaffRef,
        where('isPast', '==', false),
        orderBy('createdAt', 'desc'),
        limit(3)
      );
      
      const currentTop3Snapshot = await getDocs(currentTop3Query);
      const currentTop3Staff = await Promise.all(
        currentTop3Snapshot.docs.map(async (docSnapshot) => {
          const staffData = docSnapshot.data();
          
          // Enrich with user data
          if (staffData.userId) {
            try {
              const userDoc = await getDoc(doc(db, 'users', staffData.userId));
              if (userDoc.exists()) {
                const userData = userDoc.data();
                return {
                  id: docSnapshot.id,
                  ...staffData,
                  fullName: userData.fullName || '',
                  userName: userData.userName || '',
                  profilePictureUrl: userData.profilePictureUrl || '',
                  userSubType: userData.userSubType || '',
                  idVerificationStatus: userData.idVerificationStatus || ''
                };
              }
            } catch (error) {
              console.error(`Error fetching user data for ${staffData.userId}:`, error);
            }
          }
          
          return {
            id: docSnapshot.id,
            ...staffData
          };
        })
      );
      
      // 4.2. Get updated top 3 past staff
      const pastStaffRef = collection(db, 'users', userData.userId, 'vendorStaff');
      const pastTop3Query = query(
        pastStaffRef,
        where('isPast', '==', true),
        orderBy('createdAt', 'desc'),
        limit(3)
      );
      
      const pastTop3Snapshot = await getDocs(pastTop3Query);
      const pastTop3Staff = await Promise.all(
        pastTop3Snapshot.docs.map(async (docSnapshot) => {
          const staffData = docSnapshot.data();
          
          // Enrich with user data
          if (staffData.userId) {
            try {
              const userDoc = await getDoc(doc(db, 'users', staffData.userId));
              if (userDoc.exists()) {
                const userData = userDoc.data();
                return {
                  id: docSnapshot.id,
                  ...staffData,
                  fullName: userData.fullName || '',
                  userName: userData.userName || '',
                  profilePictureUrl: userData.profilePictureUrl || '',
                  userSubType: userData.userSubType || '',
                  idVerificationStatus: userData.idVerificationStatus || ''
                };
              }
            } catch (error) {
              console.error(`Error fetching user data for ${staffData.userId}:`, error);
            }
          }
          
          return {
            id: docSnapshot.id,
            ...staffData
          };
        })
      );
      
      // 4.3. Update main document with counts and arrays
      await updateDoc(userRef, {
        vendorStaff: currentTop3Staff,
        pastVendorStaff: pastTop3Staff,
        vendorStaffCount: increment(-1),
        pastVendorStaffCount: increment(1)
      });
      
      // 5. Update the other side of the relationship (staff's associatedVendors)
      try {
        // Get the target ID (staff userId)
        const targetId = staff.userId;
        
        if (!targetId) throw new Error('Target ID not found');
        
        // Look up associatedVendors documents for this relationship
        const associatedVendorsRef = collection(db, 'users', targetId, 'associatedVendors');
        const associatedVendorsQuery = query(associatedVendorsRef, where('vendorId', '==', userData.userId));
        
        const associatedVendorsSnapshot = await getDocs(associatedVendorsQuery);
        
        if (!associatedVendorsSnapshot.empty) {
          // Update all matching entries
          for (const docSnap of associatedVendorsSnapshot.docs) {
            await updateDoc(docSnap.ref, {
              isPast: true,
              duration: duration,
              movedToPastAt: new Date().toISOString()
            });
          }
          
          // Update the target's main document
          const staffUserRef = doc(db, 'users', targetId);
          const staffUserDoc = await getDoc(staffUserRef);
          
          if (staffUserDoc.exists()) {
            // Get new top 3 current associatedVendors
            const currentAssociatedVendorsQuery = query(
              associatedVendorsRef,
              where('isPast', '==', false),
              orderBy('createdAt', 'desc'),
              limit(3)
            );
            
            const currentAssociatedVendorsSnapshot = await getDocs(currentAssociatedVendorsQuery);
            const top3CurrentAssociatedVendors = await Promise.all(
              currentAssociatedVendorsSnapshot.docs.map(async (docSnapshot) => {
                const vendorData = docSnapshot.data();
                
                // Enrich with vendor data
                try {
                  const vendorDoc = await getDoc(doc(db, 'users', vendorData.vendorId));
                  if (vendorDoc.exists()) {
                    const vendorUserData = vendorDoc.data();
                    return {
                      id: docSnapshot.id,
                      ...vendorData,
                      fullName: vendorUserData.fullName || '',
                      userName: vendorUserData.userName || '',
                      profilePictureUrl: vendorUserData.profilePictureUrl || '',
                      userSubType: vendorUserData.userSubType || ''
                    };
                  }
                } catch (error) {
                  console.error(`Error fetching vendor data for ${vendorData.vendorId}:`, error);
                }
                
                return {
                  id: docSnapshot.id,
                  ...vendorData
                };
              })
            );
            
            // Get new top 3 past associatedVendors
            const pastAssociatedVendorsQuery = query(
              associatedVendorsRef,
              where('isPast', '==', true),
              orderBy('createdAt', 'desc'),
              limit(3)
            );
            
            const pastAssociatedVendorsSnapshot = await getDocs(pastAssociatedVendorsQuery);
            const top3PastAssociatedVendors = await Promise.all(
              pastAssociatedVendorsSnapshot.docs.map(async (docSnapshot) => {
                const vendorData = docSnapshot.data();
                
                // Enrich with vendor data
                try {
                  const vendorDoc = await getDoc(doc(db, 'users', vendorData.vendorId));
                  if (vendorDoc.exists()) {
                    const vendorUserData = vendorDoc.data();
                    return {
                      id: docSnapshot.id,
                      ...vendorData,
                      fullName: vendorUserData.fullName || '',
                      userName: vendorUserData.userName || '',
                      profilePictureUrl: vendorUserData.profilePictureUrl || '',
                      userSubType: vendorUserData.userSubType || ''
                    };
                  }
                } catch (error) {
                  console.error(`Error fetching vendor data for ${vendorData.vendorId}:`, error);
                }
                
                return {
                  id: docSnapshot.id,
                  ...vendorData
                };
              })
            );
            
            // Update target's document with updated top 3 arrays and counts
            await updateDoc(staffUserRef, {
              associatedVendors: top3CurrentAssociatedVendors,
              pastAssociatedVendors: top3PastAssociatedVendors,
              associatedVendorsCount: increment(-1),
              pastAssociatedVendorsCount: increment(1)
            });
          }
        }
      
        // Create notification for the staff
        await createNotification(targetId, 'general', {
          type: NOTIFICATION_TYPES.VENDOR_STAFF_MOVED_TO_PAST,
          title: 'Staff Relationship Moved to Past',
          message: `${userData.fullName || 'A vendor'} has moved you from their current staff to past staff.`,
          vendorId: userData.userId,
          status: 'updated',
          timestamp: new Date().toISOString(),
          data: {
            _displayData: {
              vendorName: userData.fullName,
              vendorId: userData.userId,
              duration: duration
            }
          }
        });
      } catch (err) {
        console.error('Error updating staff records:', err);
        // Continue even if this fails
      }
      
      // 6. Update UI state
      const updatedStaff = { 
        ...staff, 
        isPast: true, 
        duration: duration,
        since: undefined // Remove since property since it's now a past staff
      };
      setStaff(prev => prev.filter(s => s.id !== staff.id));
      setDisplayedStaff(prev => prev.filter(s => s.id !== staff.id));
      
      setPastStaff(prev => [updatedStaff, ...prev]);
      setDisplayedPastStaff(prev => [updatedStaff, ...prev]);
      
      // Close the modal
      setIsMoveToPastModalVisible(false);
      setSelectedStaffForMove(null);
      
      alert('Staff member successfully moved to past staff');
    } catch (error) {
      console.error('Error moving staff to past:', error);
      alert('Failed to move staff to past. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle tour selection (for RightMenu)
  const handleTourSelect = (tour) => {
    // Navigate to tour or update state as needed
  };

  // Format duration for display
  const formatDuration = (duration) => {
    if (!duration) return '';
    
    const { startMonth, startYear, endMonth, endYear } = duration;
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    return `${months[parseInt(startMonth) - 1]} ${startYear} - ${months[parseInt(endMonth) - 1]} ${endYear}`;
  };

  // Format since date for display
  const formatSince = (since) => {
    if (!since) return '';
    
    const { month, year } = since;
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    return `Since ${months[parseInt(month) - 1]} ${year}`;
  };
  
  // Generate month options for dropdowns
  const generateMonthOptions = () => {
    return [
      { value: '1', label: 'January' },
      { value: '2', label: 'February' },
      { value: '3', label: 'March' },
      { value: '4', label: 'April' },
      { value: '5', label: 'May' },
      { value: '6', label: 'June' },
      { value: '7', label: 'July' },
      { value: '8', label: 'August' },
      { value: '9', label: 'September' },
      { value: '10', label: 'October' },
      { value: '11', label: 'November' },
      { value: '12', label: 'December' }
    ];
  };

  // Generate year options for dropdowns
  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear; i >= currentYear - 50; i--) {
      years.push({ value: i.toString(), label: i.toString() });
    }
    return years;
  };

  // Staff Search Modal Component
  const StaffSearchModal = () => {
    if (!isStaffModalVisible) return null;
    
    return (
      <div className={styles.modalOverlay} onClick={() => setIsStaffModalVisible(false)}>
        <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
          {!selectedStaff ? (
            // Search interface
            <>
              <div className={styles.modalHeader}>
                <h2>Add {activeTab === 'past' ? 'Past ' : ''}Staff Member</h2>
                <button className={styles.closeButton} onClick={() => setIsStaffModalVisible(false)}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              
              <div className={styles.searchBarContainer}>
                <input
                  type="text"
                  placeholder="Search staff by name..."
                  value={staffSearchQuery}
                  onChange={(e) => setStaffSearchQuery(e.target.value)}
                  className={styles.searchInput}
                />
                <button
                  className={styles.searchButton}
                  onClick={handleStaffSearch}
                  disabled={!staffSearchQuery.trim() || searchingStaff}
                >
                  {searchingStaff ? (
                    <FontAwesomeIcon icon={faSpinner} spin />
                  ) : (
                    <FontAwesomeIcon icon={faSearch} />
                  )}
                  <span>{searchingStaff ? 'Searching...' : 'Search'}</span>
                </button>
              </div>
              
              <div className={styles.searchResults}>
                {searchingStaff ? (
                  <div className={styles.noResults}>Searching...</div>
                ) : staffResults.length > 0 ? (
                  staffResults.map((staff) => (
                    <div
                      key={staff.id}
                      className={`${styles.searchResult} ${staff.isAlreadyAdded ? styles.disabled : ''}`}
                      onClick={() => !staff.isAlreadyAdded && handleInitialSelection(staff)}
                    >
                      <img
                        src={staff.profilePictureUrl || defaultUserImage}
                        alt={staff.fullName}
                        className={styles.profilePhoto}
                      />
                      <div className={styles.userInfo}>
                        <div className={styles.nameContainer}>
                          <span className={styles.fullName}>{staff.fullName}</span>
                          {staff.idVerificationStatus === 'Verified' && (
                            <img src={verifiedBadge} alt="Verified" className={styles.verifiedBadge} />
                          )}
                        </div>
                        <span className={styles.userName}>@{staff.userName}</span>
                        <span className={styles.userSubType}>{staff.userSubType}</span>
                      </div>
                      {staff.isAlreadyAdded && (
                        <span className={styles.alreadyAddedText}>Already Added</span>
                      )}
                    </div>
                  ))
                ) : staffSearchQuery && !searchingStaff ? (
                  <div className={styles.noResults}>No staff found</div>
                ) : null}
              </div>
            </>
          ) : (
            // Details and date selection for the selected staff
            <>
              <div className={styles.modalHeader}>
                <h2>Add {selectedStaff.fullName} as {activeTab === 'past' ? 'Past ' : ''}Staff</h2>
                <button className={styles.closeButton} onClick={() => setIsStaffModalVisible(false)}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              
              <div className={styles.selectedStaffContainer}>
                <div className={styles.selectedStaffHeader}>Selected Staff:</div>
                <div className={styles.selectedStaffDetails}>
                  <img 
                    src={selectedStaff.profilePictureUrl || defaultUserImage}
                    alt={selectedStaff.fullName}
                    className={styles.profilePhoto}
                  />
                  <div className={styles.userInfo}>
                    <div className={styles.nameContainer}>
                      <span className={styles.fullName}>{selectedStaff.fullName}</span>
                      {selectedStaff.idVerificationStatus === 'Verified' && (
                        <img src={verifiedBadge} alt="Verified" className={styles.verifiedBadge} />
                      )}
                    </div>
                    <span className={styles.userName}>@{selectedStaff.userName}</span>
                    <span className={styles.userSubType}>{selectedStaff.userSubType}</span>
                  </div>
                </div>
                
                <button 
                  className={styles.backButton}
                  onClick={() => setSelectedStaff(null)}
                >
                  <FontAwesomeIcon icon={faArrowLeft} /> Back to Search
                </button>
              </div>
              
              {/* Permissions Selection */}
              <div className={styles.permissionsSection}>
  <h3>Staff Permissions</h3>
  <div className={styles.permissionsList}>
    <div className={styles.permissionItem}>
      <input
        type="checkbox"
        id="canAddItems"
        checked={staffPermissions.canAddItems}
        onChange={() => handlePermissionToggle('canAddItems')}
      />
      <label htmlFor="canAddItems">Can Add Items</label>
    </div>
    <div className={styles.permissionItem}>
      <input
        type="checkbox"
        id="canEditItems"
        checked={staffPermissions.canEditItems}
        onChange={() => handlePermissionToggle('canEditItems')}
      />
      <label htmlFor="canEditItems">Can Edit Items</label>
    </div>
    <div className={styles.permissionItem}>
      <input
        type="checkbox"
        id="canDeleteItems"
        checked={staffPermissions.canDeleteItems}
        onChange={() => handlePermissionToggle('canDeleteItems')}
      />
      <label htmlFor="canDeleteItems">Can Delete Items</label>
    </div>
    <div className={styles.permissionItem}>
      <input
        type="checkbox"
        id="canChangeStatus"
        checked={staffPermissions.canChangeStatus}
        onChange={() => handlePermissionToggle('canChangeStatus')}
      />
      <label htmlFor="canChangeStatus">Can Change Status</label>
    </div>
  </div>
</div>
              
              {/* Date Selection - only render what's appropriate for the current state */}
              <div className={styles.dateSelection}>
                <h3>{activeTab === 'past' ? 'Duration' : 'Since'}</h3>
                
                {activeTab === 'past' ? (
                  // Past Association - Start and End Dates
                  <div className={styles.dateFields}>
                    <div className={styles.dateFieldGroup}>
                      <label className={styles.fieldLabel}>Start Date:</label>
                      <div className={styles.dateInputGroup}>
                        <select 
                          className={styles.dateSelect}
                          value={startMonth}
                          onChange={(e) => setStartMonth(e.target.value)}
                          required
                        >
                          <option value="">Month</option>
                          {generateMonthOptions().map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        
                        <select 
                          className={styles.dateSelect}
                          value={startYear}
                          onChange={(e) => setStartYear(e.target.value)}
                          required
                        >
                          <option value="">Year</option>
                          {generateYearOptions().map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    
                    <div className={styles.dateFieldGroup}>
                      <label className={styles.fieldLabel}>End Date:</label>
                      <div className={styles.dateInputGroup}>
                        <select 
                          className={styles.dateSelect}
                          value={endMonth}
                          onChange={(e) => setEndMonth(e.target.value)}
                          required
                        >
                          <option value="">Month</option>
                          {generateMonthOptions().map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        
                        <select 
                          className={styles.dateSelect}
                          value={endYear}
                          onChange={(e) => setEndYear(e.target.value)}
                          required
                        >
                          <option value="">Year</option>
                          {generateYearOptions().map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                ) : (
                  // Current Association - Since Date
                  <div className={styles.dateFields}>
                    <div className={styles.dateFieldGroup}>
                      <label className={styles.fieldLabel}>Since:</label>
                      <div className={styles.dateInputGroup}>
                        <select 
                          className={styles.dateSelect}
                          value={sinceMonth}
                          onChange={(e) => setSinceMonth(e.target.value)}
                          required
                        >
                          <option value="">Month</option>
                          {generateMonthOptions().map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        
                        <select 
                          className={styles.dateSelect}
                          value={sinceYear}
                          onChange={(e) => setSinceYear(e.target.value)}
                          required
                        >
                          <option value="">Year</option>
                          {generateYearOptions().map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <div className={styles.formActions}>
                <button 
                  className={styles.addButton}
                  onClick={handleSubmitStaff}
                  disabled={(!sinceMonth && !startMonth)}
                >
                  {isProcessing ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin />
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faPlus} />
                      <span>Send Request</span>
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  // Move to Past Modal component
  const MoveToPastModal = () => {
    if (!isMoveToPastModalVisible || !selectedStaffForMove) return null;
    
    return (
      <div className={styles.modalOverlay} onClick={() => setIsMoveToPastModalVisible(false)}>
        <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
          <div className={styles.modalHeader}>
            <h2>Move to Past Staff</h2>
            <button className={styles.closeButton} onClick={() => setIsMoveToPastModalVisible(false)}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          
          <div className={styles.moveStaffInfo}>
            <div className={styles.infoIcon}>
              <FontAwesomeIcon icon={faInfoCircle} />
            </div>
            <p>
              You're about to move <strong>{selectedStaffForMove.fullName}</strong> from your current staff to past staff.
              This will update the relationship on both sides.
            </p>
            
            <div className={styles.dateInfo}>
              <div className={styles.dateItem}>
                <span className={styles.dateLabel}>From:</span>
                <span className={styles.dateValue}>
                  {selectedStaffForMove.since ? 
                    `${selectedStaffForMove.since.month}/${selectedStaffForMove.since.year}` :
                    'Not specified'}
                </span>
              </div>
              
              <div className={styles.dateArrow}>
                <FontAwesomeIcon icon={faArrowRight} />
              </div>
              
              <div className={styles.dateItem}>
                <span className={styles.dateLabel}>To:</span>
                <span className={styles.dateValue}>
                  {`${(new Date().getMonth() + 1)}/${new Date().getFullYear()}`}
                </span>
              </div>
            </div>
          </div>
          
          <div className={styles.modalActions}>
            <button 
              className={styles.cancelButton}
              onClick={() => setIsMoveToPastModalVisible(false)}
            >
              Cancel
            </button>
            
            <button 
              className={styles.confirmButton}
              onClick={confirmMoveToPast}
              disabled={isProcessing}
            >
              {isProcessing ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faCheck} />
                  <span>Confirm Move</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Edit Permissions Modal Component
  const [isEditPermissionsModalVisible, setIsEditPermissionsModalVisible] = useState(false);
  const [selectedStaffForPermissions, setSelectedStaffForPermissions] = useState(null);
  const [editingPermissions, setEditingPermissions] = useState({
    canEditProfile: false,
    canManageEvents: false,
    canManageStaff: false,
    canManageActs: false,
    canViewFinancials: false,
    canEditFinancials: false
  });

  const handleEditPermissions = (staff) => {
    setSelectedStaffForPermissions(staff);
    setEditingPermissions(staff.permissions || {
      canEditProfile: false,
      canManageEvents: false,
      canManageStaff: false,
      canManageActs: false,
      canViewFinancials: false,
      canEditFinancials: false
    });
    setIsEditPermissionsModalVisible(true);
  };

  const handlePermissionEdit = (permission) => {
    setEditingPermissions(prev => ({
      ...prev,
      [permission]: !prev[permission]
    }));
  };

  const handleSavePermissions = () => {
    if (!selectedStaffForPermissions) return;
    
    handleUpdatePermissions(selectedStaffForPermissions.id, editingPermissions);
    setIsEditPermissionsModalVisible(false);
    setSelectedStaffForPermissions(null);
  };

  const EditPermissionsModal = () => {
    if (!isEditPermissionsModalVisible || !selectedStaffForPermissions) return null;
    
    return (
      <div className={styles.modalOverlay} onClick={() => setIsEditPermissionsModalVisible(false)}>
        <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
          <div className={styles.modalHeader}>
            <h2>Edit Staff Permissions</h2>
            <button className={styles.closeButton} onClick={() => setIsEditPermissionsModalVisible(false)}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          
          <div className={styles.selectedStaffContainer}>
            <div className={styles.selectedStaffDetails}>
              <img 
                src={selectedStaffForPermissions.profilePictureUrl || defaultUserImage}
                alt={selectedStaffForPermissions.fullName}
                className={styles.profilePhoto}
              />
              <div className={styles.userInfo}>
                <div className={styles.nameContainer}>
                  <span className={styles.fullName}>{selectedStaffForPermissions.fullName}</span>
                  {selectedStaffForPermissions.idVerificationStatus === 'Verified' && (
                    <img src={verifiedBadge} alt="Verified" className={styles.verifiedBadge} />
                  )}
                </div>
                <span className={styles.userName}>@{selectedStaffForPermissions.userName}</span>
                <span className={styles.userSubType}>{selectedStaffForPermissions.userSubType}</span>
              </div>
            </div>
          </div>
          
          {/* Permissions Selection */}
          <div className={styles.permissionsSection}>
  <h3>Staff Permissions</h3>
  <div className={styles.permissionsList}>
    <div className={styles.permissionItem}>
      <input
        type="checkbox"
        id="canAddItems"
        checked={staffPermissions.canAddItems}
        onChange={() => handlePermissionToggle('canAddItems')}
      />
      <label htmlFor="canAddItems">Can Add Items</label>
    </div>
    <div className={styles.permissionItem}>
      <input
        type="checkbox"
        id="canEditItems"
        checked={staffPermissions.canEditItems}
        onChange={() => handlePermissionToggle('canEditItems')}
      />
      <label htmlFor="canEditItems">Can Edit Items</label>
    </div>
    <div className={styles.permissionItem}>
      <input
        type="checkbox"
        id="canDeleteItems"
        checked={staffPermissions.canDeleteItems}
        onChange={() => handlePermissionToggle('canDeleteItems')}
      />
      <label htmlFor="canDeleteItems">Can Delete Items</label>
    </div>
    <div className={styles.permissionItem}>
      <input
        type="checkbox"
        id="canChangeStatus"
        checked={staffPermissions.canChangeStatus}
        onChange={() => handlePermissionToggle('canChangeStatus')}
      />
      <label htmlFor="canChangeStatus">Can Change Status</label>
    </div>
  </div>
</div>
          
          <div className={styles.formActions}>
            <button 
              className={styles.cancelButton}
              onClick={() => setIsEditPermissionsModalVisible(false)}
            >
              Cancel
            </button>
            <button 
              className={styles.saveButton}
              onClick={handleSavePermissions}
              disabled={isProcessing}
            >
              {isProcessing ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faCheck} />
                  <span>Save Permissions</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

// Function to render staff permissions badges
const renderPermissionBadges = (permissions) => {
  if (!permissions) return null;
  
  const badges = [];
  
  if (permissions.canAddItems) badges.push('Add Items');
  if (permissions.canEditItems) badges.push('Edit Items');
  if (permissions.canDeleteItems) badges.push('Delete Items');
  if (permissions.canChangeStatus) badges.push('Change Status');
  
  if (badges.length === 0) return <span className={styles.noPermissions}>No special permissions</span>;
  
  return (
    <div className={styles.permissionBadges}>
      {badges.map((badge, index) => (
        <span key={index} className={styles.permissionBadge}>{badge}</span>
      ))}
    </div>
  );
};
  return (
    <div className={styles.staffContainer}>
      <div className={styles['main-layout']}>
        {!isMobile && <LeftMenu />}
        <main className={styles['staff-main']}>
          <AuthCheckWrapper>
            {hasAccess ? (
              <>
                <div className={styles.pageHeader}>
                  <h1 className={styles.pageTitle}>Staff Management</h1>
                  <p className={styles.pageDescription}>
                    Add and manage staff members for your venue and events.
                  </p>
                </div>
                
                {/* Tabs for Current/Past */}
                <div className={styles.staffTabs}>
                  <button 
                    className={`${styles.staffTab} ${activeTab === 'current' ? styles.activeTab : ''}`}
                    onClick={() => setActiveTab('current')}
                  >
                    Current
                  </button>
                  <button 
                    className={`${styles.staffTab} ${activeTab === 'past' ? styles.activeTab : ''}`}
                    onClick={() => setActiveTab('past')}
                  >
                    Past
                  </button>
                </div>
                
                <div className={styles.staffActionsSection}>
                  <div className={styles.searchContainer}>
                    <button 
                      className={styles.addStaffButton}
                      onClick={() => setIsStaffModalVisible(true)}
                    >
                      <FontAwesomeIcon icon={faUserPlus} />
                      <span>Add {activeTab === 'past' ? 'Past ' : ''}Staff</span>
                    </button>
                  </div>
                </div>
                
                <div className={styles.staffListContainer}>
                  <h2 className={styles.sectionTitle}>
                    Your {activeTab === 'past' ? 'Past ' : ''}Staff
                  </h2>
                  
                  {(activeTab === 'current' && displayedStaff.length === 0) || 
                   (activeTab === 'past' && displayedPastStaff.length === 0) ? (
                    <div className={styles.emptyState}>
                      <FontAwesomeIcon icon={faUsers} className={styles.emptyIcon} />
                      <p>You haven't added any {activeTab === 'past' ? 'past ' : ''}staff yet</p>
                    </div>
                  ) : (
                    <>
                      <div className={styles.staffList}>
                        {(activeTab === 'current' ? displayedStaff : displayedPastStaff).map((member) => (
                          <div 
                            key={member.id} 
                            className={styles.staffCard}
                          >
                            <div className={styles.staffCardContent}>
                              <div 
                                className={styles.staffInfo}
                                onClick={() => navigateToUserProfile(member.userName)}
                                style={{ cursor: 'pointer' }}
                              >
                                <img
                                  src={member.profilePictureUrl || defaultUserImage}
                                  alt={member.fullName}
                                  className={styles.staffImage}
                                />
                                <div className={styles.staffInfoText}>
                                  <div className={styles.staffNameContainer}>
                                    <span className={styles.staffName}>{member.fullName}</span>
                                    {member.idVerificationStatus === 'Verified' && (
                                      <img src={verifiedBadge} alt="Verified" className={styles.verifiedBadge} />
                                    )}
                                  </div>
                                  <span className={styles.staffUsername}>@{member.userName}</span>
                                  <span className={styles.staffType}>{member.userSubType}</span>
                                  
                                  {/* Permissions */}
                                  <div className={styles.staffPermissions}>
                                    {renderPermissionBadges(member.permissions)}
                                  </div>
                                  
                                  {/* Since or Duration badge */}
                                  {activeTab === 'current' && member.since && (
                                    <div className={styles.sinceBadge}>
                                      <FontAwesomeIcon icon={faCalendarAlt} />
                                      <span>{formatSince(member.since)}</span>
                                    </div>
                                  )}
                                  
                                  {activeTab === 'past' && member.duration && (
                                    <div className={styles.durationBadge}>
                                      <FontAwesomeIcon icon={faCalendarAlt} />
                                      <span>{formatDuration(member.duration)}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              <div className={styles.staffStatus}>
                                {member.status === 'pending' && (
                                  <span className={styles.pendingStatus}>Pending</span>
                                )}
                                {member.status === 'accepted' && (
                                  <span className={styles.acceptedStatus}>Active</span>
                                )}
                              </div>
                              
                              <div className={styles.staffActions}>
                                {activeTab === 'current' && !member.isPending && member.status === 'accepted' && (
                                  <>
                                    <button
                                      className={styles.editButton}
                                      onClick={() => handleEditPermissions(member)}
                                      title="Edit permissions"
                                    >
                                      <FontAwesomeIcon icon={faEdit} />
                                    </button>
                                    <button
                                      className={styles.moveToPastButton}
                                      onClick={() => handleMoveToPast(member.id)}
                                      title="Move to past"
                                    >
                                      <FontAwesomeIcon icon={faArrowRight} />
                                    </button>
                                  </>
                                )}
                                <button
                                  className={styles.removeButton}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveStaff(member.id);
                                  }}
                                  title="Remove staff"
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {/* Load more button */}
                      {((activeTab === 'current' && hasMoreStaff) || 
                        (activeTab === 'past' && hasMorePastStaff)) && (
                        <button 
                          className={styles.loadMoreButton}
                          onClick={loadMoreStaff}
                          disabled={isLoadingMore}
                        >
                          {isLoadingMore ? 'Loading...' : 'Load More'}
                        </button>
                      )}
                    </>
                  )}
                </div>
                
                {/* Search Modal */}
                {StaffSearchModal()}

                {/* Move to Past Modal */}
                {MoveToPastModal()}
                
                {/* Edit Permissions Modal */}
                {EditPermissionsModal()}
                
                {/* Loading Overlay */}
                {isProcessing && (
                  <div className={styles.loadingOverlay}>
                    <div className={styles.loadingSpinner}></div>
                    <div className={styles.loadingText}>
                      Processing...
                    </div>
                  </div>
                )}
              </>
            ) : (
              <PermissionDenied />
            )}
          </AuthCheckWrapper>
        </main>
        {!isMobile && <RightMenu tours={tours} onTourSelect={handleTourSelect} />}
      </div>
    </div>
  );
};

export default VendorStaff;